<script setup lang="ts">
// Tournament roll-call panel (round-29, 2026-08-31) — overlay opened from TournamentDetail.vue's
// registration phase. Walks services/rollCall.ts's state machine through the whole participant
// list: TTS-announces + shows each player in turn, accepts a voice OR tap Sí/No, then advances.
// Same "voice is never the only way out" requirement as ConfirmClaimPrompt.vue — every row is
// always tappable regardless of whether the mic hears anything.
import { computed, ref, watch } from 'vue'
import Avatar from './Avatar.vue'
import { rollCallState, answerCurrentRollCall, skipCurrentRollCall, cancelRollCall, closeRollCall, type RollCallRowStatus } from '../services/rollCall'

const isEs = computed(() => rollCallState.lang !== 'en')
const current = computed(() => (rollCallState.currentIndex >= 0 ? rollCallState.rows[rollCallState.currentIndex] : null))
const finished = computed(() => rollCallState.active && rollCallState.currentIndex === -1 && rollCallState.rows.length > 0)
const question = computed(() => {
  const name = current.value?.player.displayName ?? ''
  return isEs.value ? `${name}, ¿estás listo?` : `${name}, are you ready?`
})

const statusLabel: Record<RollCallRowStatus, string> = {
  pending: '·',
  asking: '…',
  confirmed: '✓',
  denied: '✗',
  timeout: '⏱',
  skipped: '⤼',
}
const statusClass: Record<RollCallRowStatus, string> = {
  pending: 'text-neutral-600',
  asking: 'text-amber-400 animate-pulse',
  confirmed: 'text-emerald-400',
  denied: 'text-red-400',
  timeout: 'text-neutral-500',
  skipped: 'text-neutral-500',
}

const confirmedCount = computed(() => rollCallState.rows.filter((r) => r.status === 'confirmed').length)

// Countdown bar — same convention as ConfirmClaimPrompt.vue's.
const barEl = ref<HTMLElement | null>(null)
watch(
  () => rollCallState.deadlineTs,
  (deadline) => {
    if (deadline == null || !barEl.value) return
    const remainingMs = Math.max(0, deadline - Date.now())
    const el = barEl.value
    el.style.transition = 'none'
    el.style.width = '100%'
    void el.offsetWidth
    requestAnimationFrame(() => {
      el.style.transition = `width ${remainingMs}ms linear`
      el.style.width = '0%'
    })
  },
  { immediate: true },
)

function close() {
  if (rollCallState.status === 'awaiting') cancelRollCall()
  else closeRollCall()
}
</script>

<template>
  <div v-if="rollCallState.active" class="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 px-4">
    <div class="w-full max-w-sm rounded-2xl bg-neutral-900 border border-neutral-700 shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
      <div class="h-1.5 bg-neutral-800 shrink-0">
        <div ref="barEl" class="h-full bg-amber-500" :style="{ width: rollCallState.status === 'awaiting' ? '100%' : '0%' }" />
      </div>

      <!-- Current ask -->
      <div v-if="current && rollCallState.status === 'awaiting'" class="p-5 flex flex-col items-center gap-3 text-center shrink-0 border-b border-neutral-800">
        <Avatar :player="current.player" :size="64" />
        <p class="text-lg font-bold">{{ question }}</p>
        <p v-if="current.speakerMismatch" class="text-xs text-amber-400">
          {{ isEs ? 'Aviso: la voz no coincide claramente — respuesta aceptada igualmente' : 'Note: voice doesn’t clearly match — answer accepted anyway' }}
        </p>
        <div class="flex gap-3 w-full mt-1">
          <button
            type="button"
            class="tap-target flex-1 rounded-xl bg-neutral-800 active:bg-neutral-700 text-base font-bold"
            @click="answerCurrentRollCall(null, 'no', 'tap')"
          >
            {{ isEs ? 'No' : 'No' }}
          </button>
          <button
            type="button"
            class="tap-target flex-1 rounded-xl bg-emerald-600 active:bg-emerald-500 text-base font-bold"
            @click="answerCurrentRollCall(null, 'yes', 'tap')"
          >
            {{ isEs ? 'Sí' : 'Yes' }}
          </button>
        </div>
        <button type="button" class="text-xs text-neutral-500 underline underline-offset-2" @click="skipCurrentRollCall">
          {{ isEs ? 'Saltar' : 'Skip' }}
        </button>
      </div>

      <div v-else-if="finished" class="p-5 text-center shrink-0 border-b border-neutral-800">
        <p class="text-lg font-bold">
          {{ isEs ? `Listado: ${confirmedCount}/${rollCallState.rows.length} listos` : `Roll call: ${confirmedCount}/${rollCallState.rows.length} ready` }}
        </p>
      </div>

      <!-- Full list -->
      <div class="flex-1 min-h-0 overflow-y-auto px-4 py-3 flex flex-col gap-1">
        <div
          v-for="(row, i) in rollCallState.rows"
          :key="row.player.id"
          class="flex items-center gap-3 rounded-lg px-2 py-1.5"
          :class="i === rollCallState.currentIndex ? 'bg-neutral-800' : ''"
        >
          <Avatar :player="row.player" :size="32" />
          <span class="flex-1 truncate text-sm">{{ row.player.displayName }}</span>
          <span class="w-5 text-center font-bold" :class="statusClass[row.status]">{{ statusLabel[row.status] }}</span>
        </div>
      </div>

      <div class="p-3 border-t border-neutral-800 shrink-0">
        <button type="button" class="tap-target w-full rounded-xl bg-neutral-800 active:bg-neutral-700 font-semibold" @click="close">
          {{ finished ? (isEs ? 'Cerrar' : 'Close') : isEs ? 'Cancelar' : 'Cancel' }}
        </button>
      </div>
    </div>
  </div>
</template>
